/**
 * Which messages go out on which channel, and when reminders fire.
 *
 * One component for both surfaces — organisation settings and a
 * campaign's settings tab — because the policy is the same shape either
 * way and only the inheritance badge differs.
 *
 * Two sections rather than one table, deliberately. A booking
 * confirmation is sent BECAUSE SOMETHING HAPPENED and its timing is not
 * a choice; a reminder is sent BECAUSE A CLOCK RAN DOWN and its timing is
 * the whole point. A single table meant a "When" column that was an
 * editable field on four rows and fixed grey text on the other four,
 * which read as broken.
 *
 * Contract notes that are easy to break: reminder ids must round-trip
 * (see notifyPolicy.ts), and clearing both channels disables the message
 * rather than falling back to email.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";

import {
  EVENT_ORDER,
  type Policy,
  type PolicyResponse,
  type Reminder,
  type Tone,
  draftId,
  estimate,
  offsetProblems,
  sortReminders,
  splitOffset,
  toMinutes,
  toPayload,
} from "./notifyPolicy";

export type PolicyScope = { kind: "org" } | { kind: "campaign"; id: string };

function urlFor(scope: PolicyScope): string {
  return scope.kind === "org"
    ? ApiPaths.orgNotifyPolicy
    : ApiPaths.campaignNotifyPolicy(scope.id);
}

export function NotifyPolicyEditor({ scope }: { scope: PolicyScope }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const url = urlFor(scope);
  const key = ["notify-policy", scope.kind, scope.kind === "campaign" ? scope.id : "me"];

  const loaded = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get<PolicyResponse>(url)).data,
  });

  const [draft, setDraft] = useState<Policy | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed only while clean, so a refetch mid-edit cannot silently
  // discard what the operator has typed.
  useEffect(() => {
    if (loaded.data && !dirty) setDraft(loaded.data.policy);
  }, [loaded.data, dirty]);

  const save = useMutation({
    mutationFn: async (policy: Policy) =>
      (await api.put<PolicyResponse>(url, toPayload(policy))).data,
    onSuccess: (data) => {
      setDraft(data.policy);
      setDirty(false);
      setError(null);
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setError(detail ?? t("notify.saveFailed"));
    },
  });

  const reset = useMutation({
    mutationFn: async () => (await api.delete<PolicyResponse>(url)).data,
    onSuccess: (data) => {
      setDraft(data.policy);
      setDirty(false);
      setError(null);
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  if (loaded.isLoading || !draft || !loaded.data) {
    return <p className="text-sm text-nb-text-3">{t("common.loading")}</p>;
  }

  // Bound after the guard above: TypeScript's narrowing of `draft` does
  // not reach the handler closures defined below, so they need a
  // definitely-non-null reference rather than a `!` at each use.
  const policy: Policy = draft;
  const meta = loaded.data;
  const smsOff = !meta.org_sms_enabled;
  const reminders = sortReminders(policy.reminders);
  const problems = offsetProblems(reminders, meta.min_offset_minutes);
  const est = estimate(
    policy,
    meta.estimate?.beneficiaries ?? 0,
    meta.org_sms_enabled,
  );

  function edit(next: Policy) {
    setDraft(next);
    setDirty(true);
    setError(null);
  }

  function setEventChannel(kind: string, channel: "email" | "sms", on: boolean) {
    const flags = policy.channels[kind] ?? { email: true, sms: false };
    edit({
      ...policy,
      channels: { ...policy.channels, [kind]: { ...flags, [channel]: on } },
    });
  }

  function patchReminder(id: string, patch: Partial<Reminder>) {
    edit({
      ...policy,
      reminders: policy.reminders.map((r) =>
        r.id === id ? { ...r, ...patch } : r,
      ),
    });
  }

  function addReminder() {
    edit({
      ...policy,
      reminders: [
        ...policy.reminders,
        {
          id: draftId(),
          offset_minutes: 30,
          tone: "starting_soon",
          email: true,
          // A new reminder is a new cost the operator is choosing.
          sms: false,
        },
      ],
    });
  }

  function removeReminder(id: string) {
    edit({ ...policy, reminders: policy.reminders.filter((r) => r.id !== id) });
  }

  const blocked = problems.size > 0;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display text-base font-semibold text-nb-text">
          {t("notify.title")}
        </h3>
        <p className="mt-1 max-w-prose text-sm text-nb-text-2">
          {t("notify.intro")}
        </p>
      </div>

      {smsOff && (
        <p
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300"
          data-testid="notify-sms-disabled"
        >
          {t("notify.smsDisabledForOrg")}
        </p>
      )}

      {scope.kind === "campaign" && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span
            className="rounded-full border border-slate-600 px-2 py-0.5 font-mono text-nb-text-3"
            data-testid="notify-inheritance"
          >
            {meta.inherited ? t("notify.inherited") : t("notify.ownPolicy")}
          </span>
          {!meta.inherited && (
            <button
              type="button"
              className="text-nb-mint hover:underline"
              onClick={() => reset.mutate()}
              disabled={reset.isPending}
              data-testid="notify-reset"
            >
              {t("notify.resetToOrg")}
            </button>
          )}
        </div>
      )}

      {/* ---- messages: timing is not a choice ---- */}
      <section className="space-y-2">
        <header className="flex flex-wrap items-baseline justify-between gap-2">
          <h4 className="font-display text-sm font-semibold text-nb-text">
            {t("notify.messagesTitle")}
          </h4>
          <span className="text-xs text-nb-text-3">
            {t("notify.messagesWhy")}
          </span>
        </header>
        <div className="overflow-x-auto rounded-md border border-slate-700">
          <table className="w-full min-w-[34rem] text-sm">
            <thead>
              <tr className="text-left text-[0.65rem] uppercase tracking-wider text-nb-text-3">
                <th className="px-3 py-2 font-medium">{t("notify.colMessage")}</th>
                <th className="px-3 py-2 font-medium">{t("notify.colSent")}</th>
                <th className="w-20 px-3 py-2 text-center font-medium">
                  {t("notify.colEmail")}
                </th>
                <th className="w-20 px-3 py-2 text-center font-medium">
                  {t("notify.colSms")}
                </th>
              </tr>
            </thead>
            <tbody>
              {EVENT_ORDER.map((kind) => {
                const flags = policy.channels[kind] ?? { email: true, sms: false };
                return (
                  <tr key={kind} className="border-t border-slate-700">
                    <td className="px-3 py-2 font-medium text-nb-text">
                      {t(`notify.kind.${kind}`)}
                    </td>
                    <td className="px-3 py-2 text-xs text-nb-text-3">
                      {t(`notify.trigger.${kind}`)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        aria-label={`${t(`notify.kind.${kind}`)} — ${t("notify.colEmail")}`}
                        checked={flags.email !== false}
                        onChange={(e) =>
                          setEventChannel(kind, "email", e.target.checked)
                        }
                        data-testid={`notify-${kind}-email`}
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        aria-label={`${t(`notify.kind.${kind}`)} — ${t("notify.colSms")}`}
                        checked={Boolean(flags.sms)}
                        disabled={smsOff}
                        onChange={(e) =>
                          setEventChannel(kind, "sms", e.target.checked)
                        }
                        data-testid={`notify-${kind}-sms`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---- reminders: timing is the point ---- */}
      <section className="space-y-2">
        <header className="flex flex-wrap items-baseline justify-between gap-2">
          <h4 className="font-display text-sm font-semibold text-nb-text">
            {t("notify.remindersTitle")}
          </h4>
          <span className="text-xs text-nb-text-3">
            {t("notify.remindersWhy")}
          </span>
        </header>

        <ReminderStrip reminders={reminders} />

        <div className="overflow-x-auto rounded-md border border-slate-700">
          <table className="w-full min-w-[38rem] text-sm">
            <thead>
              <tr className="text-left text-[0.65rem] uppercase tracking-wider text-nb-text-3">
                <th className="px-3 py-2 font-medium">{t("notify.colBefore")}</th>
                <th className="px-3 py-2 font-medium">{t("notify.colWording")}</th>
                <th className="w-20 px-3 py-2 text-center font-medium">
                  {t("notify.colEmail")}
                </th>
                <th className="w-20 px-3 py-2 text-center font-medium">
                  {t("notify.colSms")}
                </th>
                <th className="w-10 px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {reminders.map((r) => {
                const { value, unit } = splitOffset(r.offset_minutes);
                const problem = problems.get(r.id);
                return (
                  <tr key={r.id} className="border-t border-slate-700">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          min={1}
                          className="input w-20 py-1 text-sm"
                          aria-label={t("notify.colBefore")}
                          value={value}
                          onChange={(e) =>
                            patchReminder(r.id, {
                              offset_minutes: toMinutes(
                                Number(e.target.value || 0),
                                unit,
                              ),
                            })
                          }
                          data-testid={`notify-reminder-value-${r.id}`}
                        />
                        <select
                          className="input py-1 text-sm"
                          aria-label={t("notify.colUnit")}
                          value={unit}
                          onChange={(e) =>
                            patchReminder(r.id, {
                              offset_minutes: toMinutes(value, e.target.value),
                            })
                          }
                          data-testid={`notify-reminder-unit-${r.id}`}
                        >
                          <option value="minutes">{t("notify.unitMinutes")}</option>
                          <option value="hours">{t("notify.unitHours")}</option>
                          <option value="days">{t("notify.unitDays")}</option>
                        </select>
                      </div>
                      {problem && (
                        <p className="mt-1 text-[0.7rem] text-cvp-danger">
                          {t(`notify.problem.${problem}`)}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="input py-1 text-sm"
                        aria-label={t("notify.colWording")}
                        value={r.tone}
                        onChange={(e) =>
                          patchReminder(r.id, { tone: e.target.value as Tone })
                        }
                        data-testid={`notify-reminder-tone-${r.id}`}
                      >
                        <option value="day_before">{t("notify.toneDayBefore")}</option>
                        <option value="starting_soon">
                          {t("notify.toneStartingSoon")}
                        </option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        aria-label={t("notify.colEmail")}
                        checked={r.email}
                        onChange={(e) =>
                          patchReminder(r.id, { email: e.target.checked })
                        }
                        data-testid={`notify-reminder-email-${r.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        aria-label={t("notify.colSms")}
                        checked={r.sms}
                        disabled={smsOff}
                        onChange={(e) =>
                          patchReminder(r.id, { sms: e.target.checked })
                        }
                        data-testid={`notify-reminder-sms-${r.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        className="text-nb-text-3 hover:text-cvp-danger"
                        aria-label={t("notify.removeReminder")}
                        onClick={() => removeReminder(r.id)}
                        data-testid={`notify-reminder-remove-${r.id}`}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
              {reminders.length === 0 && (
                <tr className="border-t border-slate-700">
                  <td colSpan={5} className="px-3 py-3 text-xs text-nb-text-3">
                    {t("notify.noReminders")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <button
          type="button"
          className="rounded-md border border-dashed border-nb-mint/40 px-3 py-1.5 text-xs text-nb-mint"
          onClick={addReminder}
          data-testid="notify-add-reminder"
        >
          {t("notify.addReminder")}
        </button>
      </section>

      {/* ---- what it costs ---- */}
      <div
        className="flex flex-wrap gap-6 rounded-md border border-slate-700 bg-nb-surface-2 px-4 py-3"
        data-testid="notify-estimate"
      >
        <Metric label={t("notify.estBeneficiaries")} value={est.beneficiaries} />
        <Metric label={t("notify.estEmails")} value={est.emails} />
        <Metric label={t("notify.estSms")} value={est.sms} cost />
      </div>
      <p className="max-w-prose text-[0.7rem] leading-snug text-nb-text-3">
        {t("notify.estCaveat")}
      </p>

      {error && <p className="text-sm text-cvp-danger">{error}</p>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => save.mutate(policy)}
          disabled={!dirty || blocked || save.isPending}
          data-testid="notify-save"
        >
          {save.isPending ? t("common.saving") : t("common.save")}
        </button>
        {blocked && (
          <span className="text-xs text-cvp-danger">
            {t("notify.fixBeforeSaving")}
          </span>
        )}
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  cost,
}: {
  label: string;
  value: number;
  cost?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={`font-mono text-xl tabular-nums ${
          cost ? "text-amber-300" : "text-nb-mint"
        }`}
      >
        {value.toLocaleString()}
      </span>
      <span className="font-mono text-[0.65rem] uppercase tracking-wider text-nb-text-3">
        {label}
      </span>
    </div>
  );
}

/**
 * Where each reminder sits between booking and the appointment, so "how
 * often are we texting these people" is answerable without reading the
 * table. SMS-enabled ones are amber.
 *
 * Positioned on a log scale: on a linear one a 24-hour reminder and a
 * 5-minute reminder would sit on top of each other at the right-hand
 * end, which is the case the strip exists to make legible.
 */
function ReminderStrip({ reminders }: { reminders: Reminder[] }) {
  const { t } = useTranslation();
  if (reminders.length === 0) return null;
  const max = Math.max(...reminders.map((r) => r.offset_minutes), 1);

  return (
    <div className="relative mb-6 mt-1 h-12 border-b border-slate-700">
      {reminders.map((r) => {
        // 0% = furthest out, 100% = the appointment.
        const pct =
          100 - (Math.log(r.offset_minutes + 1) / Math.log(max + 1)) * 100;
        const { value, unit } = splitOffset(r.offset_minutes);
        return (
          <span
            key={r.id}
            className="absolute bottom-2 flex -translate-x-1/2 flex-col items-center gap-1"
            style={{ left: `${Math.min(96, Math.max(4, pct))}%` }}
          >
            <span
              className={`font-mono text-[0.62rem] whitespace-nowrap ${
                r.sms ? "text-amber-300" : "text-nb-text-2"
              }`}
            >
              {value}
              {t(`notify.abbr.${unit}`)}
            </span>
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                r.sms ? "bg-amber-400" : "bg-nb-text-3"
              }`}
            />
          </span>
        );
      })}
      <span className="absolute -bottom-5 left-0 font-mono text-[0.62rem] text-nb-text-3">
        {t("notify.stripBooking")}
      </span>
      <span className="absolute -bottom-5 right-0 font-mono text-[0.62rem] text-nb-mint">
        {t("notify.stripAppointment")}
      </span>
    </div>
  );
}
